import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Team from "./components/team/Team.tsx";
import DataFile from "./components/dataFile/DataFile.tsx";
import Branding from "./components/branding/Branding.tsx";
import { NotebookPage } from "../notebook/NotebookPage.tsx";
import { engagementStore } from "./engagementStore.ts";
import { resetEngagementScopedState } from "./engagementScopedState.ts";
import ContentStep from "./components/phases/content/ContentStep.tsx";
import CampaignStep from "./components/phases/campaign/CampaignStep.tsx";
import AdGroupsStep from "./components/phases/adGroups/AdGroupsStep.tsx";
import AudienceStep from "./components/phases/audience/AudienceStep.tsx";
import { Monitor } from "./components/phases/monitor/Monitor.tsx";
import { Close } from "./components/phases/close/Close.tsx";
import { EngagementService } from "@services/engagements/engagementService.ts";
import { Schedule } from "./components/phases/schedule/Schedule.tsx";
import EmailSmsStep from "./components/phases/emailSms/EmailSmsStep";
import { PublishActions } from "./components/phases/publish/PublishActions.tsx";
import { Engagement } from "@models/engagement.model.ts";
import { Budget } from "./components/phases/budget/Budget.tsx";
import { Budget2 } from "./components/phases/budget/Budget2.tsx";
import { EngagementContractTab } from "./components/phases/contract/engagementContractTab.tsx";
import { engagementEvents } from "./engagementEvents.ts";
import { useSnapshot } from "valtio";
import { Paths } from "../../paths.tsx";
import Loader from "../../components/common/loader/loader.tsx";
import { EngagementHeader } from "./components/EngagementHeader.tsx";
import AggregateVersionsDrawer from "@/shared/components/versions/AggregateVersionsDrawer";
import { scopeLabelForSlug, sectionsForSlug } from "./workflow/versionSections";
import {
  EngagementActivityEntry,
  EngagementConfigVersion,
  ENGAGEMENT_TRIGGER_LABELS,
} from "@models/engagementConfigVersion.model";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { WorkflowSidebar } from "./components/workflow/WorkflowSidebar.tsx";
import { WorkflowOverview } from "./components/workflow/WorkflowOverview.tsx";
import { refreshSetupStatus, workflowStore } from "./workflow/workflowStore.ts";
import { workflowEvents } from "./workflow/workflowEvents.ts";
import OrganizationNetworkResourcesTab from "../organizations/components/OrganizationNetworkAccountsTab.tsx";
import OrganizationSftpTab from "../organizations/sftp/components/OrganizationSftpTab.tsx";
import { getContentWidth, getWorkflowItemBySlug, OVERVIEW_SLUG } from "./workflow/workflowItems.ts";
import { useWorkflowAccess } from "./workflow/useWorkflowAccess.ts";
import { WorkflowItemNotAvailable } from "./components/workflow/WorkflowItemNotAvailable.tsx";
import "./components/workflow/engagementLayout.scss";
import ReturnToJob from "@components/ReturnToJob/ReturnToJob";

const EngagementDetailPage = () => {
  const { engagementId, tab } = useParams<{ engagementId: string; tab: string }>();
  const navigate = useNavigate();
  const { search } = useLocation();

  // Config history (MPN-1334). One drawer for the whole engagement, scoped to the page you are
  // standing on: the scheduler shows schedule changes, the budget page budget changes. One
  // snapshot, filtered reads — not a history button per page.
  const [versions, setVersions] = useState<EngagementConfigVersion[]>([]);
  const [activity, setActivity] = useState<EngagementActivityEntry[]>([]);
  const [versionsOpen, setVersionsOpen] = useState(false);
  /* Just the number for the History badge. Kept apart from `versions` because they are
     fetched by different endpoints for different reasons: the count is one query and is
     wanted immediately, the list diffs every consecutive pair of snapshots and is wanted
     only when someone opens the drawer. */
  const [versionCount, setVersionCount] = useState<number | undefined>(undefined);

  const engagementStoreSnap = useSnapshot(engagementStore);
  const workflowEventsSnap = useSnapshot(workflowEvents);
  const engagementEventsSnap = useSnapshot(engagementEvents);
  // The store's default is a blank Engagement (id -1), not null, so a plain
  // null check would render the whole workflow around an engagement that was
  // never loaded — every item then fetching against id -1 and coming back empty.
  const loadedEngagement = engagementStoreSnap.selectedEngagement;
  const selectedEngagement = loadedEngagement?.id > 0 ? loadedEngagement : null;

  /* Restricted while the segmented Budget screen is under test (MPN-2041). Resolved from
     the workflow definition rather than naming the feature again here, so the rail and
     the route cannot drift apart. */
  const canSeeWorkflowItem = useWorkflowAccess();
  const budgetTwoItem = getWorkflowItemBySlug(selectedEngagement?.type ?? "REACH", "budget-2");
  const canSeeBudgetTwo = !!budgetTwoItem && canSeeWorkflowItem(budgetTwoItem);
  const emailSmsItem = getWorkflowItemBySlug(selectedEngagement?.type ?? "REACH", "email-sms");
  const canSeeEmailSms = !!emailSmsItem && canSeeWorkflowItem(emailSmsItem);

  useDocumentTitle(
    selectedEngagement?.name
      ? `${selectedEngagement.name}${tab ? ` · ${tab}` : ""}`
      : "Engagement"
  );

  const [engagementName, setEngagementName] = useState("");
  const [editingEngagementName, setEditingEngagementName] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const parsedEngagementId = Number(engagementId);
  const hasValidEngagementId = Number.isFinite(parsedEngagementId) && parsedEngagementId > 0;

  useEffect(() => {
    if (!hasValidEngagementId) {
      setIsLoading(false);
      return;
    }
    getEngagement(parsedEngagementId);
  }, [engagementId]);

  /*
      Note the visit, for Last Visited and the Recently Visited sort (MPN-1720).

      Here rather than on the list's row click, because this is what "opening an engagement"
      actually means: arriving at its detail page, however you got there -- a row, a todo, a
      Slack link, a bookmark. Recording it on the click would miss every one of those and
      would also count a click that failed to load anything.

      Keyed on the engagement id alone, so moving between its tabs does not re-stamp it.
  */
  useEffect(() => {
    if (!hasValidEngagementId) return;
    void new EngagementService().recordEngagementView(parsedEngagementId);
  }, [parsedEngagementId, hasValidEngagementId]);

  useEffect(() => {
    const id = Number(engagementId);
    if (!id) return;
    refreshSetupStatus(id);
  }, [engagementId, workflowEventsSnap.WORKFLOW_DATA_TOUCHED]);

  useEffect(() => {
    const id = Number(engagementId);
    if (!id) return;
    const handleFocus = () => {
      if (workflowStore.loadFailed) {
        refreshSetupStatus(id);
      }
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [engagementId]);

  const loadVersions = useCallback(() => {
    const id = Number(engagementId);
    if (!id || Number.isNaN(id)) return;
    const service = new EngagementService();
    service
      .listConfigVersions(id)
      .then(setVersions)
      // History is forensics, never the point of the page: a failure here must not blank the
      // engagement, so the drawer just opens empty.
      .catch(() => setVersions([]));
    // Separately, so a failure in one does not take the other with it. The Activity tab is a
    // second question the reader has to ask for.
    service
      .getConfigActivity(id)
      .then(setActivity)
      .catch(() => setActivity([]));
  }, [engagementId]);

  /* The badge, on page load. This is the cheap endpoint -- one COUNT, no snapshots and no
     diffing -- so it does not reintroduce the cost that took the version LIST off page
     load. Left undefined on failure: HistoryButton hides the badge when it has no number,
     and history is forensics, never the point of the page. */
  const loadVersionCount = useCallback(() => {
    const id = Number(engagementId);
    if (!id || Number.isNaN(id)) return;
    new EngagementService()
      .countConfigVersions(id)
      .then(setVersionCount)
      .catch(() => setVersionCount(undefined));
  }, [engagementId]);

  useEffect(() => {
    loadVersionCount();
  }, [loadVersionCount]);

  // Only when the drawer is open. Rendering the history is expensive — every version carries a
  // full snapshot and the differ walks both sides of each consecutive pair — and it used to run
  // on every engagement page load, purely to put a number on a button nobody had clicked. It is
  // also the load that has to be correct: every other trigger depends on the editing component
  // remembering to fire an event and several do not (the header rename sends
  // ENGAGEMENT_NAME_UPDATED, the Budget page sends nothing), so opening the drawer is the one
  // moment the data is certainly being read, whatever wrote it.
  useEffect(() => {
    if (versionsOpen) loadVersions();
  }, [versionsOpen, loadVersions]);

  // Refresh while it is open, so a save made behind the drawer appears without reopening it.
  // Guarded on `versionsOpen` for the same reason as above: closed, there is nothing to update.
  useEffect(() => {
    if (versionsOpen) loadVersions();
  }, [
    workflowEventsSnap.WORKFLOW_DATA_TOUCHED,
    engagementEventsSnap.ENGAGEMENT_NAME_UPDATED,
    engagementEventsSnap.ENGAGEMENT_STATUS_UPDATED,
  ]);

  /* The badge tracks the same signals, drawer open or not: an edit made on the page should
     move the number without a reload. Cheap enough to run on each, unlike the list. */
  useEffect(() => {
    loadVersionCount();
  }, [
    workflowEventsSnap.WORKFLOW_DATA_TOUCHED,
    engagementEventsSnap.ENGAGEMENT_NAME_UPDATED,
    engagementEventsSnap.ENGAGEMENT_STATUS_UPDATED,
  ]);

  useEffect(() => {
    if (engagementId && !tab) {
      // Carry the query string onto the default tab. Dropping it lost `?from=`, so a
      // link in from a job landed here with no way back -- and any other deep link to a
      // bare engagement URL loses its params the same way (MPN-1636).
      navigate(
        `${import.meta.env.BASE_URL}${Paths.ENGAGEMENT}/${engagementId}/${OVERVIEW_SLUG}${search}`,
        { replace: true }
      );
    }
  }, [engagementId, tab, navigate, search]);

  useEffect(() => {
    if (selectedEngagement?.name) {
      setEngagementName(selectedEngagement.name);
    }
  }, [selectedEngagement?.name]);

  async function getEngagement(id: number) {
    setIsLoading(true);
    // Before the fetch, not after: every selection and cached payload still in
    // the stores belongs to whichever engagement was open last.
    resetEngagementScopedState();
    try {
      const service = new EngagementService();
      const engagement = await service.getEngagement(id);
      engagementStore.selectedEngagement = engagement;
    } catch (error) {
      console.error("Get Engagement Details:", error);
      // Don't leave the previous engagement on screen under this one's URL.
      engagementStore.selectedEngagement = new Engagement();
    } finally {
      setIsLoading(false);
    }
  }

  const handleUpdateEngagementName = async () => {
    if (!selectedEngagement) return;
    try {
      const service = new EngagementService();
      await service.updateEngagementName(selectedEngagement.id, engagementName);
    } catch (error) {
      console.error("Update Engagement Name:", error);
    } finally {
      setEditingEngagementName(false);
      engagementEvents.send_event("ENGAGEMENT_NAME_UPDATED");
    }
  };

  const handleCancelEditEngagementName = () => {
    if (!selectedEngagement) return;
    setEngagementName(selectedEngagement.name);
    setEditingEngagementName(false);
  };

  const handleBack = () => {
    const params = engagementStore.listSearchParams;
    navigate(`${import.meta.env.BASE_URL}${Paths.ENGAGEMENT}${params ? `?${params}` : ""}`);
  };

  const handleNavigate = (slug: string) => {
    // Carries `?from=` with it, like the default-tab redirect above: the first click
    // in the sidebar otherwise threw away the way back to the job that linked here.
    navigate(`${import.meta.env.BASE_URL}${Paths.ENGAGEMENT}/${engagementId}/${slug}${search}`);
  };

  const renderItem = (slug: string, engagement: Engagement) => {
    switch (slug) {
      case "contract":
        return <EngagementContractTab />;
      case "network-resources":
        return (
          <OrganizationNetworkResourcesTab
            organizationId={engagement.client?.id ?? 0}
            onResourcesChanged={() => workflowEvents.send_event("WORKFLOW_DATA_TOUCHED")}
          />
        );
      case "sftp":
        return (
          <OrganizationSftpTab
            organizationId={engagement.client?.id ?? 0}
            onKeysChanged={() => workflowEvents.send_event("WORKFLOW_DATA_TOUCHED")}
          />
        );
      case "branding":
        return <Branding />;
      case "team":
        return <Team />;
      case "data-file":
        return <DataFile />;
      case "campaign":
      case "setup": // old bookmark for the pre-split Setup step
        return <CampaignStep />;
      case "audience":
        return <AudienceStep />;
      case "ad-groups":
        return <AdGroupsStep />;
      case "content":
        return <ContentStep />;
      case "schedule":
        return <Schedule />;
      // Checked here as well as in the rail, for the reason Budget 2 gives above: hiding a
      // nav item is not access control, and the URL is shareable. The server gates these
      // screens' reads and writes on the same feature.
      case "email-sms":
        return canSeeEmailSms ? <EmailSmsStep /> : <WorkflowItemNotAvailable />;
      case "budget":
        return <Budget />;
      // Its own component. Same data, through the same hook — see Budget2's own note.
      // Checked here as well as in the rail: hiding a nav item is not access control, and
      // the URL is shareable. The server gates this screen's writes on the same feature,
      // so this is about not showing someone a page they cannot use.
      case "budget-2":
        return canSeeBudgetTwo ? <Budget2 /> : <WorkflowItemNotAvailable />;
      case "publish":
        return <PublishActions />;
      case "monitor":
        return <Monitor />;
      case "close":
        return <Close />;
      case "notes":
        return engagement.notebook ? (
          <NotebookPage notebookId={engagement.notebook.id} />
        ) : (
          <div className="text-center text-muted mt-5">No notebook for this engagement.</div>
        );
      default:
        return (
          <WorkflowOverview
            engagementType={engagement.type}
            onNavigate={handleNavigate}
          />
        );
    }
  };

  if (!hasValidEngagementId) {
    return (
      <div className="px-3 py-4">
        <div className="alert alert-danger py-2 small">This engagement link is invalid.</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="px-3 py-4">
        <Loader />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column h-100" style={{ backgroundColor: "#F0F1F6" }}>
      <ReturnToJob />
      {selectedEngagement && (
        <EngagementHeader
          engagement={selectedEngagement as Engagement}
          engagementName={engagementName}
          editingEngagementName={editingEngagementName}
          onEngagementNameChange={setEngagementName}
          onSubmitEngagementName={handleUpdateEngagementName}
          onStartEditName={() => setEditingEngagementName(true)}
          onCancelEditName={handleCancelEditEngagementName}
          onBack={handleBack}
          // No count on the button. Producing one meant rendering the whole history on every
          // page load, and a count that only appears after the first open is worse than none —
          // the button would look like it had found new history each time you returned.
          onOpenHistory={() => setVersionsOpen(true)}
          historyCount={versionCount}
        />
      )}

      <div
        className="flex-grow-1 d-flex flex-column"
        style={{ backgroundColor: "#F0F1F6", overflow: "hidden", padding: "16px 24px 24px", minHeight: 0 }}
      >
        <div
          className="flex-grow-1 d-flex"
          style={{ background: "#fff", borderRadius: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", overflow: "hidden", minHeight: 0 }}
        >
          {!selectedEngagement ? (
            <div className="w-100 p-3">
              <div className="alert alert-danger py-2 small mb-0">
                Could not load this engagement.
              </div>
            </div>
          ) : (
            <>
              <WorkflowSidebar
                engagementType={selectedEngagement.type}
                activeSlug={tab ?? OVERVIEW_SLUG}
                onNavigate={handleNavigate}
                onOpenJobs={() => navigate(`${import.meta.env.BASE_URL}${Paths.JOBS}`)}
              />
              <div className="engagement-content">
                <div
                  className={`engagement-content__inner engagement-content__inner--${getContentWidth(
                    tab ?? OVERVIEW_SLUG
                  )}`}
                >
                  {renderItem(tab ?? OVERVIEW_SLUG, selectedEngagement as Engagement)}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <AggregateVersionsDrawer
        open={versionsOpen}
        onClose={() => setVersionsOpen(false)}
        versions={versions}
        title={
          scopeLabelForSlug(tab)
            ? `${scopeLabelForSlug(tab)} history`
            : "Config history"
        }
        subtitle={engagementName}
        emptyMessage="Nothing recorded yet. Editing the engagement config creates the first entry."
        triggerLabels={ENGAGEMENT_TRIGGER_LABELS}
        sections={sectionsForSlug(tab)}
        activity={activity}
      />
    </div>
  );
};

export default EngagementDetailPage;
